import { useCallback, useMemo } from "react"

import {
  selectCollateralPrice,
  selectMinimumCollateralRatio,
  selectUserLoan,
  useDappSelector,
} from "#/redux-state"
import {
  bigIntToHumanReadableFormat,
  separateThousandsWithComma,
} from "#/shared/utils"

export const useCollateralPrice = (collateralAmount: bigint) => {
  const collateralPrice = useDappSelector(selectCollateralPrice)

  const collateralPriceLabelWithUSD = useMemo(() => {
    if (!collateralPrice || !collateralAmount) return "0"

    const price = (collateralPrice * collateralAmount) / 10n ** 18n

    const priceLabel = separateThousandsWithComma(
      bigIntToHumanReadableFormat(price),
      2,
    )

    return price < 0n ? `-$${priceLabel.slice(1)}` : `$${priceLabel}`
  }, [collateralAmount, collateralPrice])

  return { collateralPrice, collateralPriceLabelWithUSD }
}

export const useCalculateLiquidationPrice = () => {
  // Liquidations in recovery mode are still using MCR
  const minimumCollateralRatio = useDappSelector(selectMinimumCollateralRatio)

  const collateralPrice = useDappSelector(selectCollateralPrice)

  const calculateLiquidationPrice = useCallback(
    (debt: bigint, collateral: bigint) => {
      if (!minimumCollateralRatio || !collateralPrice || !collateral) return 0n

      const liquidationPrice = (debt * minimumCollateralRatio) / collateral

      return liquidationPrice
    },
    [collateralPrice, minimumCollateralRatio],
  )

  return { calculateLiquidationPrice }
}

export const useChangeInLiquidationPriceOnCollateralChange = (
  prevCollateral: bigint,
  newCollateral: bigint,
) => {
  const userLoan = useDappSelector(selectUserLoan)
  const { calculateLiquidationPrice } = useCalculateLiquidationPrice()

  const prevLiquidationPrice = useMemo(() => {
    if (!prevCollateral) return 0n

    return calculateLiquidationPrice(userLoan?.debtAmount ?? 0n, prevCollateral)
  }, [prevCollateral, calculateLiquidationPrice, userLoan?.debtAmount])

  const prevLiquidationPriceLabel = useMemo(
    () =>
      separateThousandsWithComma(
        bigIntToHumanReadableFormat(prevLiquidationPrice),
        2,
      ),
    [prevLiquidationPrice],
  )

  const newLiquidationPrice = useMemo(() => {
    if (!newCollateral) return 0n

    return calculateLiquidationPrice(userLoan?.debtAmount ?? 0n, newCollateral)
  }, [newCollateral, calculateLiquidationPrice, userLoan?.debtAmount])

  const newLiquidationPriceLabel = useMemo(
    () =>
      separateThousandsWithComma(
        bigIntToHumanReadableFormat(newLiquidationPrice),
        2,
      ),
    [newLiquidationPrice],
  )

  const changeInLiquidationPrice = useMemo(
    () =>
      prevLiquidationPrice && newLiquidationPrice
        ? newLiquidationPrice - prevLiquidationPrice
        : 0n,
    [prevLiquidationPrice, newLiquidationPrice],
  )

  const changeInLiquidationPriceLabelWithUSD = useMemo(() => {
    const price = separateThousandsWithComma(
      bigIntToHumanReadableFormat(changeInLiquidationPrice),
      2,
    )

    return changeInLiquidationPrice >= 0n ? `$${price}` : `-$${price.slice(1)}`
  }, [changeInLiquidationPrice])

  return {
    prevLiquidationPrice,
    prevLiquidationPriceLabel,
    newLiquidationPrice,
    newLiquidationPriceLabel,
    changeInLiquidationPrice,
    changeInLiquidationPriceLabelWithUSD,
  }
}

export const useChangeInLiquidationPriceOnDebtChange = (
  prevDebt: bigint,
  newDebt: bigint,
) => {
  const userLoan = useDappSelector(selectUserLoan)
  const { calculateLiquidationPrice } = useCalculateLiquidationPrice()

  const prevLiquidationPrice = useMemo(() => {
    if (!prevDebt) return 0n

    return calculateLiquidationPrice(prevDebt, userLoan?.collateralAmount ?? 0n)
  }, [prevDebt, calculateLiquidationPrice, userLoan?.collateralAmount])

  const prevLiquidationPriceLabel = useMemo(
    () =>
      separateThousandsWithComma(
        bigIntToHumanReadableFormat(prevLiquidationPrice),
        2,
      ),
    [prevLiquidationPrice],
  )

  const newLiquidationPrice = useMemo(() => {
    if (!newDebt) return 0n

    return calculateLiquidationPrice(newDebt, userLoan?.collateralAmount ?? 0n)
  }, [newDebt, calculateLiquidationPrice, userLoan?.collateralAmount])

  const newLiquidationPriceLabel = useMemo(
    () =>
      separateThousandsWithComma(
        bigIntToHumanReadableFormat(newLiquidationPrice),
        2,
      ),
    [newLiquidationPrice],
  )

  const changeInLiquidationPrice = useMemo(
    () =>
      prevLiquidationPrice && newLiquidationPrice
        ? newLiquidationPrice - prevLiquidationPrice
        : 0n,
    [prevLiquidationPrice, newLiquidationPrice],
  )

  const changeInLiquidationPriceLabelWithUSD = useMemo(() => {
    const price = separateThousandsWithComma(
      bigIntToHumanReadableFormat(changeInLiquidationPrice),
      2,
    )

    return changeInLiquidationPrice >= 0n ? `$${price}` : `-$${price.slice(1)}`
  }, [changeInLiquidationPrice])

  return {
    prevLiquidationPrice,
    prevLiquidationPriceLabel,
    newLiquidationPrice,
    newLiquidationPriceLabel,
    changeInLiquidationPrice,
    changeInLiquidationPriceLabelWithUSD,
  }
}

export const useCurrentLiquidationPrice = () => {
  const userLoan = useDappSelector(selectUserLoan)

  const { calculateLiquidationPrice } = useCalculateLiquidationPrice()

  const liquidationPrice = useMemo(() => {
    if (!userLoan) return 0n

    const { debtAmount, collateralAmount } = userLoan

    return calculateLiquidationPrice(debtAmount, collateralAmount)
  }, [userLoan, calculateLiquidationPrice])

  const liquidationPriceLabel = useMemo(
    () =>
      separateThousandsWithComma(
        bigIntToHumanReadableFormat(liquidationPrice),
        2,
      ),
    [liquidationPrice],
  )

  return { liquidationPrice, liquidationPriceLabel }
}

export const useLiquidationPrice = (debt: bigint, collateral: bigint) => {
  const { calculateLiquidationPrice } = useCalculateLiquidationPrice()

  const liquidationPrice = calculateLiquidationPrice(debt, collateral)

  const liquidationPriceLabel = useMemo(
    () =>
      separateThousandsWithComma(
        bigIntToHumanReadableFormat(liquidationPrice),
        2,
      ),
    [liquidationPrice],
  )

  return { liquidationPrice, liquidationPriceLabel }
}
